import localForage from 'localforage'
import { hasOwnProp } from '@/api/ApiHelpers'
import {
  OFFLINE_FIELD_STORES,
  persistOfflinePushRequests
} from '@/api/offlineCache'
import { sanitizeFarmunitPayload } from '@/api/FarmunitPayload'
import { sanitizeOrderPayload } from '@/api/OrderPayload'
import { sanitizeUsagePayload } from '@/api/UsagePayload'

const hasOfflineRequestError = (result) => {
  return !result || result?.error || result?.response?.data?.errors || result?.isAxiosError
}

const responsePayload = (result) => {
  if (!result) {
    return null
  }

  return hasOwnProp(result, 'data') ? result.data : result
}

const responseModelId = (result) => {
  const payload = responsePayload(result)
  return payload && typeof payload === 'object' ? payload.id : null
}

const offlineErrorStatus = result => result?.error?.status || result?.response?.status || null

const offlineErrorMessage = (result) => {
  return result?.error?.data?.message ||
    result?.error?.message ||
    result?.response?.data?.message ||
    result?.message ||
    ''
}

const isIdempotentDeleteSuccess = (req, result) => {
  if (!req || req.type !== 'delete' || !hasOfflineRequestError(result)) {
    return false
  }

  const message = offlineErrorMessage(result)
  return offlineErrorStatus(result) === 404 ||
    /No query results for model/i.test(message)
}

const formatOfflineError = (result, synchronizationId = null) => {
  const validationErrors = result?.response?.data?.errors || result?.error?.data?.errors
  if (validationErrors) {
    return Object.values(validationErrors)
      .flat()
      .filter(item => item)
      .join(', ')
  }

  return result?.error?.data?.message ||
    result?.error?.message ||
    result?.response?.data?.message ||
    result?.message ||
    `Please Contact Support.${synchronizationId ? (' Error ID: ' + synchronizationId) : ''}`
}

const requestStateForAttempt = (req, status, errorData = '') => ({
  ...req,
  status,
  error_data: errorData,
  last_attempted_at: new Date().toISOString(),
  attempt_count: Number(req.attempt_count || 0) + (status === 'sending' ? 1 : 0)
})

const restoreExclusionForRequest = (req) => {
  if (['create', 'subCreate'].includes(req.type)) {
    return { state: req.state, id: req.requestId }
  }

  if (req.type === 'delete') {
    return { state: req.state, id: req.payload?.id }
  }

  return null
}

const recordOfflineAudit = (dispatch, rootState, payload = {}) => {
  if (!rootState.offlineAudit) {
    return null
  }

  return dispatch('offlineAudit/record', payload, { root: true })
}

const persistQueue = async (nuxtContext, state) => {
  const localForageClient = nuxtContext?.$localForage || localForage
  try {
    await persistOfflinePushRequests(localForageClient, state.offlinePushRequests)
  } catch (error) {
    console.warn('Unable to persist offline request queue.', error)
  }
}

const sanitizeQueuedPayload = (req, payload) => {
  const state = req.state
  if (state === 'farmunits') {
    return sanitizeFarmunitPayload(payload)
  }
  if (state === 'orders') {
    return sanitizeOrderPayload(payload, {
      defaultCompleted: req.type === 'create',
      includeWaterYear: req.type === 'create'
    })
  }
  if (state === 'usages') {
    return sanitizeUsagePayload(payload)
  }

  return payload
}

export const state = () => ({
  appUpdateAvailable: false,
  snack: '',
  snackSettings: {
    color: 'secondary',
    top: false,
    timeout: 4000,
    right: true
  },
  history: [],
  activeView: false,
  loadStatus: 0,
  loadCount: 0,
  pastView: [],
  validateFields: {},
  showFormErrors: false,
  waterYear: null,
  availableWaterYears: [],
  customComponent: null,
  offlinePushRequests: [],
  offlineSyncSummary: null,
  offlineCacheManifest: null,
  offlineCacheStatus: {
    ready: false,
    checkedAt: null,
    savedAt: null,
    waterYear: null,
    userId: null,
    storeCounts: {},
    missingStores: [],
    relationIssues: []
  },
  offlineCachePersistPaused: false,
  cachedData: [],
  loadStores: OFFLINE_FIELD_STORES
})
export const actions = {
  async pushSync ({ commit, state, dispatch, rootState }) {
    const offlinePushRequests = [...state.offlinePushRequests]
    await recordOfflineAudit(dispatch, rootState, {
      event: 'offline_push_started',
      details: {
        queued_count: offlinePushRequests.length
      }
    })
    if (offlinePushRequests.length > 0) {
      const pushSync = await this.$repositories.synchronizations.create({})
      if (hasOwnProp(pushSync, 'error')) {
        await recordOfflineAudit(dispatch, rootState, {
          event: 'offline_push_preflight_failed',
          severity: 'error',
          details: {
            queued_count: offlinePushRequests.length,
            error: pushSync.error?.message || pushSync.error?.data?.message || 'Unable to create synchronization.'
          }
        })
        return { ok: false, error: pushSync.error, processed: 0 }
      }

      const results = []
      const succeededRequestIds = []
      const failedRequestIds = []
      const skippedRequestIds = []
      const restoreExclusions = []
      for (const req of offlinePushRequests) {
        if (hasOwnProp(req, 'allowPush') && req.allowPush === false) {
          commit('UPDATE_OFFLINE_REQUEST', {
            ...req,
            status: 'skipped',
            error_data: ''
          })
          await persistQueue(this, state)
          await recordOfflineAudit(dispatch, rootState, {
            event: 'offline_request_held',
            severity: 'warning',
            details: {
              request_id: req.requestId,
              state: req.state,
              type: req.type,
              synchronization_id: pushSync.data.id
            }
          })
          skippedRequestIds.push(req.requestId)
          results.push({ requestId: req.requestId, skipped: true })
        } else {
          const result = await dispatch('sendRequest', { req, pushSync })
          if (hasOfflineRequestError(result)) {
            failedRequestIds.push(req.requestId)
          } else {
            succeededRequestIds.push(req.requestId)
            const restoreExclusion = restoreExclusionForRequest(req)
            if (restoreExclusion && restoreExclusion.id !== undefined && restoreExclusion.id !== null) {
              restoreExclusions.push(restoreExclusion)
            }
          }
          results.push({ requestId: req.requestId, result })
        }
      }
      await dispatch('syncStats/show', 1, { root: true })
      const summary = {
        ok: failedRequestIds.length === 0 && state.offlinePushRequests.every(req => req.status !== 'error'),
        complete: failedRequestIds.length === 0 && skippedRequestIds.length === 0 && state.offlinePushRequests.length === 0,
        processed: results.length,
        results,
        succeededRequestIds,
        failedRequestIds,
        skippedRequestIds,
        restoreExclusions
      }
      commit('SET_OFFLINE_SYNC_SUMMARY', summary)
      await recordOfflineAudit(dispatch, rootState, {
        event: summary.complete ? 'offline_push_completed' : 'offline_push_incomplete',
        severity: summary.complete ? 'info' : 'warning',
        details: {
          processed: summary.processed,
          succeeded_count: succeededRequestIds.length,
          failed_count: failedRequestIds.length,
          skipped_count: skippedRequestIds.length
        }
      })
      return summary
    }
    const summary = {
      ok: true,
      complete: true,
      processed: 0,
      results: [],
      succeededRequestIds: [],
      failedRequestIds: [],
      skippedRequestIds: [],
      restoreExclusions: []
    }
    commit('SET_OFFLINE_SYNC_SUMMARY', summary)
    await recordOfflineAudit(dispatch, rootState, {
      event: 'offline_push_completed',
      details: {
        processed: 0,
        succeeded_count: 0,
        failed_count: 0,
        skipped_count: 0
      }
    })
    return summary
  },

  async sendRequest ({ commit, state, dispatch, rootState }, { req, pushSync }) {
    if (!req) {
      return {
        error: {
          status: 404,
          data: {
            message: 'Offline request was not found on this device.'
          }
        }
      }
    }
    let result = {}
    commit('UPDATE_OFFLINE_REQUEST', requestStateForAttempt(req, 'sending'))
    if (req.type === 'subCreate') {
      result = await this.$repositories[req.payload.type].subCreate(
        req.payload.id,
        { ...req.payload.resource, offline_request_id: req.requestId }
      )
    } else if (req.type === 'create') {
      result = await this.$repositories[req.state].create(
        sanitizeQueuedPayload(req, { ...req.payload, offline_request_id: req.requestId })
      )
    } else if (req.type === 'update') {
      result = await this.$repositories[req.state].update({
        id: req.payload.id,
        payload: sanitizeQueuedPayload(req, { ...req.payload.payload, offline_request_id: req.requestId })
      })
    } else if (req.type === 'delete') {
      result = await this.$repositories[req.state].delete(req.payload.id, { offline_request_id: req.requestId })
    } else if (req.type === 'detach') {
      result = await this.$repositories[req.payload.apiPath].subDelete(
        req.payload.parentId,
        req.payload.childId,
        { offline_request_id: req.requestId }
      )
    } else {
      result = {
        error: {
          status: 422,
          data: {
            message: `Unsupported offline request type: ${req.type}`
          }
        }
      }
    }

    const logObject = { ...req }

    if (pushSync) {
      logObject.synchronization_id = pushSync.data.id
    }

    if (isIdempotentDeleteSuccess(req, result)) {
      logObject.idempotent_delete = true
      result = {
        data: {
          id: req.payload?.id,
          already_deleted: true
        }
      }
    }

    if (hasOfflineRequestError(result)) {
      logObject.status = 'error'
      logObject.errorData = formatOfflineError(result, logObject.synchronization_id)
      logObject.attempt_count = Number(req.attempt_count || 0) + 1

      const errMessage = logObject.errorData || 'Offline request failed.'

      await commit('UPDATE_OFFLINE_REQUEST', {
        ...req,
        status: 'error',
        error_data: errMessage,
        last_attempted_at: new Date().toISOString(),
        last_error_at: new Date().toISOString(),
        attempt_count: Number(req.attempt_count || 0) + 1,
        allowPush: true
      })
      await persistQueue(this, state)

      dispatch('snackbar/showMessage', {
        content: {
          message: errMessage,
          status: result?.response?.status || result?.error?.status || 500
        },
        color: 'error'
      }, {
        root: true
      })
      await recordOfflineAudit(dispatch, rootState, {
        event: 'offline_request_failed',
        severity: 'error',
        details: {
          request_id: req.requestId,
          state: req.state,
          type: req.type,
          synchronization_id: logObject.synchronization_id || null,
          error: errMessage
        }
      })
    } else {
      if (['create', 'subCreate'].includes(req.type)) {
        logObject.created_model_id = responseModelId(result)
      }
      await commit('REMOVE_OFFLINE_REQUEST', req.requestId)
      await commit(req.state + '/REMOVE', req.requestId, { root: true })
      await persistQueue(this, state)
      logObject.status = 'success'
      await recordOfflineAudit(dispatch, rootState, {
        event: 'offline_request_uploaded',
        details: {
          request_id: req.requestId,
          state: req.state,
          type: req.type,
          synchronization_id: logObject.synchronization_id || null,
          created_model_id: logObject.created_model_id || null
        }
      })
    }
    if (this.$repositories.offlineRequests) {
      await this.$repositories.offlineRequests.create(logObject)
    }

    return result
  },

  async retryRequests ({ commit, state, dispatch, rootState }, requestIds = []) {
    const ids = requestIds.length
      ? requestIds
      : state.offlinePushRequests.filter(req => req.status === 'error').map(req => req.requestId)
    const requests = state.offlinePushRequests.filter(req => ids.includes(req.requestId))
    await recordOfflineAudit(dispatch, rootState, {
      event: 'offline_retry_started',
      details: {
        request_ids: ids,
        retry_count: requests.length
      }
    })
    const results = []
    const succeededRequestIds = []
    const failedRequestIds = []

    for (const req of requests) {
      const result = await dispatch('sendRequest', {
        req: {
          ...req,
          allowPush: true
        }
      })
      results.push({ requestId: req.requestId, result })
      if (hasOfflineRequestError(result)) {
        failedRequestIds.push(req.requestId)
      } else {
        succeededRequestIds.push(req.requestId)
      }
    }

    const summary = {
      ok: failedRequestIds.length === 0,
      complete: failedRequestIds.length === 0,
      processed: results.length,
      results,
      succeededRequestIds,
      failedRequestIds,
      skippedRequestIds: [],
      restoreExclusions: []
    }
    commit('SET_OFFLINE_SYNC_SUMMARY', summary)
    return summary
  },

  resetPassword ({ commit, state }, payload) {
    return this.$repositories.forgotPassword.create(payload)
  },
  async navBack ({ commit, state }) {
    const newActive = state.pastView[1]
    await commit('NAVIGATE_ACTIVE_DIALOG_BACK')
    return newActive
  },
  setWaterYear ({ commit, dispatch, rootState }, value) {
    // Set system year value
    commit('SET_WATER_YEAR', value)
    const om = Object.values(rootState.omsettings.all).find(
      om => parseInt(om.year) === parseInt(value)
    )

    // Object to use in filters based on selected year
    const yearObject = {
      start: om ? om.water_year_start : `${value}-01-01`,
      end: om ? om.water_year_end : `${value}-12-31`
    }

    // Loop through all stores available in vuex and set filters accordingly
    for (const [k, v] of Object.entries(rootState)) {
      const existingFilter = v.filter
      const newFilter = {}

      // Find fields of the store object effected by year and add that to filter
      if (hasOwnProp(v, 'effectedByYear') && v.effectedByYear) {
        if (hasOwnProp(v, 'yearFields') && v.yearFields.length > 0) {
          const attrBetweenDate = {}
          for (const item of v.yearFields) {
            attrBetweenDate[item] = yearObject
          }
          newFilter.attr_between_date = attrBetweenDate
        }

        // Find relations of the store object and add their fields effected by year to filter
        if (hasOwnProp(v, 'relations') && v.relations.length > 0) {
          const obj = {}

          for (const item of Object.values(v.relations)) {
            if (
              hasOwnProp(item, 'yearFields') &&
                            item.yearFields.length > 0
            ) {
              const items = {
                attr_between_date: {}
              }
              for (const el of item.yearFields) {
                items.attr_between_date[el] = yearObject
              }
              obj[item.translate] = items
            }
          }

          if (Object.values(obj).length > 0) {
            newFilter.relations = obj
          }
        }

        commit(
                    `${k}/replaceFilter`,
                    { ...existingFilter, ...newFilter },
                    { root: true }
        )
      }
    }
  }
}
export const mutations = {
  handle_error (state, snack) {
    state.snack = snack
  },
  set_validateFields: (state, validateFields) => {
    state.validateFields = validateFields
  },
  set_showFormErrors: (state, showFormErrors) => {
    state.showFormErrors = showFormErrors
  },
  handle_errorSettings: (state, snackSettings) => {
    state.snackSettings = snackSettings
  },
  NAVIGATE_ACTIVE_DIALOG_BACK (state) {
    state.pastView.splice(0, 2)
  },
  pushHistory (state, route) {
    state.history.push(route)
  },
  popHistory (state) {
    state.history.pop()
  },
  SET_APP_UPDATE_AVAILABLE (state, value) {
    state.appUpdateAvailable = Boolean(value)
  },
  SET_HISTORY (state, payload) {
    state.pastView.unshift(payload)
  },
  SET_LOADCOUNT (state, payload) {
    state.loadCount = payload
  },
  SET_LOADSTATUS (state, payload) {
    state.loadStatus = payload
  },
  FLUSH_HISTORY (state) {
    state.pastView = []
    state.history = []
  },
  SET_DYNAMIC (state, resource) {
    state[resource.key] = resource.value
  },

  SET_CUSTOM_COMPONENT (state, componentName) {
    state.customComponent = componentName
  },

  SET_ACTIVE_VIEW (state, active) {
    state.activeView = active
  },

  SET_SNACK (state, snack) {
    state.snack = snack
  },

  set_snackSettings: (state, snackSettings) => {
    state.snackSettings = snackSettings
  },

  SET_ALL_WATER_YEARS (state, payload) {
    state.availableWaterYears = Object.values(payload)
  },

  SET_OFFLINE_CACHE_MANIFEST (state, payload) {
    state.offlineCacheManifest = payload
  },

  SET_OFFLINE_CACHE_STATUS (state, payload) {
    state.offlineCacheStatus = {
      ...state.offlineCacheStatus,
      ...payload
    }
  },

  SET_OFFLINE_SYNC_SUMMARY (state, payload) {
    state.offlineSyncSummary = payload
  },

  SET_OFFLINE_CACHE_PERSIST_PAUSED (state, payload) {
    state.offlineCachePersistPaused = Boolean(payload)
  },

  SET_WATER_YEAR (state, payload) {
    state.waterYear = payload
  },

  PUT_OFFLINE_REQUEST (state, payload) {
    const offlinePushRequests = state.offlinePushRequests
    offlinePushRequests.push(payload)
    state.offlinePushRequests = offlinePushRequests
  },

  REPLACE_ALL_OFFLINE_REQUESTS (state, payload) {
    state.offlinePushRequests = payload
  },

  UPDATE_OFFLINE_REQUEST (state, payload) {
    const offlinePushRequests = state.offlinePushRequests
    const item = offlinePushRequests.findIndex(
      i => i.requestId === payload.requestId
    )
    if (item === -1) {
      offlinePushRequests.push(payload)
    } else {
      offlinePushRequests.splice(item, 1, payload)
    }
    state.offlinePushRequests = offlinePushRequests
  },

  REMOVE_OFFLINE_REQUEST (state, requestId) {
    state.offlinePushRequests = state.offlinePushRequests.filter(
      or => or.requestId !== requestId
    )
  }
}
export const getters = {
  permissions (state, commit, rootState) {
    let permissions = []
    permissions = rootState.auth.user.data.can
    return permissions
  },
  activeUser (state, commit, rootState) {
    let user = {}
    if (rootState.auth.user.data) {
      user = rootState.auth.user.data
    }

    return user
  },
  roles (state, commit, rootState) {
    let roles = []
    if (rootState.auth.user.data) {
      roles = rootState.auth.user.data.roles
    }
    return roles
  },
  isReadonly (state, getters) {
    return getters.roles.includes('readonly')
  },
  getWaterYear (state) {
    return state.waterYear
  },
  getAllWaterYears (state) {
    return state.availableWaterYears
  },
  getLatestWaterYear (state) {
    const newOrder = JSON.parse(JSON.stringify(state.availableWaterYears))
    newOrder.sort()
    newOrder.reverse()
    return newOrder[0]
  },
  offlineRequests (state) {
    return state.offlinePushRequests
  },
  getPastView (state) {
    return _.cloneDeep(state.pastView)
  }
}
